import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import ModalContext from 'contexts/ModalContext'
import { OfferPageActions, generateOfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { getMaxGuestCount } from 'lib/offer/occupancyUtils'

import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import OfferCapacityWithAgesVerticalSelection from '../../CapacityWithAges/OfferCapacityWithAgesVerticalSelection'
import ModalBase from 'LuxKit/Modal/ModalBase'
import OfferCapacityWithAgesVerticalSidebarSelection from '../../CapacityWithAges/OfferCapacityWithAgesVerticalSidebarSelection'
import OfferCapacityWithAgesSelection from '../../CapacityWithAges/OfferCapacityWithAgesSelection'
import OfferCalendarCapacityChip from './OfferCalendarCapacityChip'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'

interface Props {
  offer: App.Offer | App.BedbankOffer | App.Tours.TourV2Offer
  rooms?: Array<App.Occupants>
  open: boolean
  disableMultiRoom?: boolean
  capacities?: Array<App.RoomCapacity>
  occupantUnit?: 'guest' | 'traveller'
  variant?: 'modal' | 'dropdown'
}
const defaultRooms = [generateOfferPageRoom()]

function OfferCalendarCapacitySelect(props: Props) {
  const {
    offer,
    open,
    disableMultiRoom,
    capacities,
    occupantUnit = 'guest',
    rooms = defaultRooms,
    variant = 'dropdown',
  } = props
  const intl = useIntl()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const isMobile = useScreenSizeOnly('mobile')
  const showModal = useContext(ModalContext)
  const [localRooms, setLocalRooms] = useState<Array<App.RoomOccupants | App.Occupants>>(rooms)

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const dispatch = useOfferPageDispatch()

  const saveRooms = useCallback(() => {
    dispatch({
      type: OfferPageActions.setRooms,
      rooms: localRooms as any,
    })
  }, [dispatch, localRooms])

  const onToggleOpen = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'capacity' })
    if (open) {
      onUserAnalyticsAction(CALENDAR_GA_EVENT_ACTION.CloseSubFilter, CALENDAR_GA_EVENT_LABEL.OpenCapacity)
    } else {
      onUserAnalyticsAction(CALENDAR_GA_EVENT_ACTION.OpenSubFilter, CALENDAR_GA_EVENT_LABEL.OpenCapacity)
    }
  }, [dispatch, onUserAnalyticsAction, open])

  const onClose = useCallback(() => {
    saveRooms()
    dispatch({ type: OfferPageActions.closeSubFilter })
    onUserAnalyticsAction(CALENDAR_GA_EVENT_ACTION.CloseSubFilter, CALENDAR_GA_EVENT_LABEL.OpenCapacity)
  }, [dispatch, onUserAnalyticsAction, saveRooms])

  const maxGuests = useMemo((): number => getMaxGuestCount(capacities), [capacities])

  useEffect(() => {
    if (open && variant === 'modal') {
      if (disableMultiRoom) {
        showModal(<ModalBase height="max">
          <OfferCapacityWithAgesVerticalSidebarSelection
            footerText="Apply"
            disableMultiRoom
          />
        </ModalBase>,
        ).then(() => {
          onClose()
        })
      } else {
        showModal(<ModalBase height="max">
          <ModalHeader title={intl.formatMessage({
            id: 'BvblpW41z2W6vd90_hDP',
            defaultMessage: 'Who is going?',
            description: 'Title for guest capacity selection modal on offer calendar',
          })}
          />
          <ModalBody>
            <OfferCapacityWithAgesSelection capacities={capacities} />
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: intl.formatMessage({
                id: '4zN4ddw9ZyeSjxwBdrwt',
                defaultMessage: 'Apply',
                description: 'Button to apply guest capacity selection on offer calendar',
              }),
              onClick: (e, resolve) => resolve(),
            }}
          />
        </ModalBase>).then(() => {
          dispatch({ type: OfferPageActions.closeSubFilter })
        })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const onDropdownSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    saveRooms()
    onClose()
  }, [onClose, saveRooms])

  const formId = useId()

  const capacityDropdownTitle = intl.formatMessage({
    id: 'o303wItFfq5GjHdlEb2F',
    defaultMessage: 'Who is going?',
    description: 'Title for guest capacity selection dropdown on offer calendar',
  })
  const capacityDropdownSubtitle = maxGuests > 0
    ? occupantUnit === 'traveller'
      ? intl.formatMessage(
          {
            id: '3eUo3OBDzkN93MOAw2-5',
            defaultMessage: 'Maximum occupancy is {maxGuests, plural, one {# traveller} other {# travellers}}',
            description: 'Subtitle showing maximum traveller occupancy for offer calendar capacity dropdown',
          },
          { maxGuests },
        )
      : intl.formatMessage(
          {
            id: 'V6U7W9FX7VoUugbRl_cL',
            defaultMessage: 'Maximum occupancy is {maxGuests, plural, one {# guest} other {# guests}}',
            description: 'Subtitle showing maximum guest occupancy for offer calendar capacity dropdown',
          },
          { maxGuests },
        )
    : undefined
  const capacityDropdownApply = intl.formatMessage({
    id: '9mNOJlNfPKc0uNupUETm',
    defaultMessage: 'Apply',
    description: 'Button to apply guest capacity in offer calendar dropdown',
  })

  return (<>
    <OfferCalendarCapacityChip
      selected={open}
      ref={triggerRef}
      onClick={onToggleOpen}
      offer={offer}
      disableMultiRoom={disableMultiRoom}
    />
    {variant === 'dropdown' && <DropdownSheet
      size="M"
      open={open && !isMobile}
      hasDismissButton
      onClose={onClose}
      anchorRef={triggerRef}
      triggerRef={triggerRef}
      data-testid="offer-calendar-capacity-select-dropdown"
      title={capacityDropdownTitle}
      subtitle={capacityDropdownSubtitle}
      primaryActionProps={{
        children: capacityDropdownApply,
        onClick: onClose,
      }}
    >
      <form onSubmit={onDropdownSubmit} id={formId}>
        <OfferCapacityWithAgesVerticalSelection
          capacities={capacities}
          disableMultiRoom={disableMultiRoom}
          onChange={setLocalRooms}
        />
      </form>
    </DropdownSheet>}
  </>
  )
}

export default withOfferPageState((state) => {
  return {
    offer: state.offer ?? state.bedbankOffer ?? state.tourV2Offer,
    rooms: state.rooms,
    open: state.activeSubFilter === 'capacity',
  }
}, OfferCalendarCapacitySelect)
